import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { trackAddToCart, trackRemoveFromCart, trackViewCart, trackBeginCheckout } from "../utils/analytics";

const BasketContext = createContext(null);

export function BasketProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoValid, setPromoValid] = useState(null);
  const prevItemCountRef = useRef(0);

  // Auto-open drawer when a NEW item is added (most reliable method)
  useEffect(() => {
    if (items.length > prevItemCountRef.current) {
      setIsOpen(true);
    }
    prevItemCountRef.current = items.length;
  }, [items.length]);

  // Lock body scroll when basket drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const addItem = useCallback((product) => {
    let wasAdded = false;
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.product_id);
      if (existing) return prev;
      wasAdded = true;
      // Track GA4 add_to_cart event
      trackAddToCart({
        id: product.product_id,
        name: product.name,
        price: product.price,
        category: product.product_id?.startsWith("care-") ? "Care Plan" : "SmartSite™",
      }, 1);
      return [...prev, { ...product, quantity: 1 }];
    });
    // Force open drawer after React commits the items update
    // setTimeout ensures AnimatePresence correctly handles the mount
    setTimeout(() => {
      setIsOpen(true);
    }, 0);
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => {
      const item = prev.find((i) => i.product_id === productId);
      if (item) {
        // Track GA4 remove_from_cart event
        trackRemoveFromCart({
          id: item.product_id,
          name: item.name,
          price: item.price,
          category: item.product_id?.startsWith("care-") ? "Care Plan" : "SmartSite™",
        }, 1);
      }
      return prev.filter((i) => i.product_id !== productId);
    });
  }, []);

  const clearBasket = useCallback(() => {
    setItems([]);
    setPromoCode("");
    setPromoDiscount(0);
    setPromoValid(null);
  }, []);

  const isInBasket = useCallback(
    (productId) => items.some((i) => i.product_id === productId),
    [items]
  );

  // Track view_cart when basket opens
  const openBasket = useCallback(() => {
    setIsOpen(true);
    const cartTotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    trackViewCart(items.map(item => ({
      id: item.product_id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      category: item.product_id?.startsWith("care-") ? "Care Plan" : "SmartSite™",
    })), cartTotal);
  }, [items]);

  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  // SAVE10 does NOT apply to Care Plans (recurring services)
  const discountableSubtotal = items.filter((i) => !i.product_id?.startsWith("care-")).reduce((sum, i) => sum + i.price * i.quantity, 0);
  const discount = promoDiscount > 0 ? Math.round(discountableSubtotal * (promoDiscount / 100) * 100) / 100 : 0;
  const total = Math.round((subtotal - discount) * 100) / 100;
  const itemCount = items.length;

  const applyPromo = useCallback(async (code) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/promo/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (data.valid) {
        setPromoCode(data.code);
        setPromoDiscount(data.discount_pct);
        setPromoValid(true);
      } else {
        setPromoValid(false);
        setPromoDiscount(0);
      }
      return data;
    } catch {
      setPromoValid(false);
      return { valid: false };
    }
  }, []);

  const checkout = useCallback(async (customerEmail, customerPhone) => {
    // Track GA4 begin_checkout event
    trackBeginCheckout(items.map(item => ({
      id: item.product_id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      category: item.product_id?.startsWith("care-") ? "Care Plan" : "SmartSite\u2122",
    })), total, promoCode);
    
    const API = process.env.REACT_APP_BACKEND_URL;
    let res;
    try {
      res = await fetch(`${API}/api/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
          promo_code: promoCode || null,
          origin_url: window.location.origin,
          customer_email: customerEmail || null,
          customer_phone: customerPhone || null,
        }),
      });
    } catch {
      throw new Error("Network error. Please check your connection and try again.");
    }
    if (!res.ok) {
      let msg = "Checkout failed. Please try again.";
      try {
        const errData = await res.json();
        if (errData.detail) msg = typeof errData.detail === "string" ? errData.detail : JSON.stringify(errData.detail);
      } catch {}
      throw new Error(msg);
    }
    const data = await res.json();
    if (!data.url) throw new Error("No checkout URL received. Please try again.");
    window.location.href = data.url;
  }, [items, promoCode, total]);

  return (
    <BasketContext.Provider
      value={{
        items, addItem, removeItem, clearBasket, isInBasket,
        isOpen, setIsOpen, openBasket, itemCount,
        subtotal, discount, total,
        promoCode, promoDiscount, promoValid, applyPromo,
        checkout,
      }}
    >
      {children}
    </BasketContext.Provider>
  );
}

export const useBasket = () => useContext(BasketContext);
